'use client';

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  User, 
  Globe, 
  Crown, 
  CaretRight,
  Check,
  Upload,
  Spinner,
  Key,
  Users,
  Ticket,
  WarningCircle,
} from '@phosphor-icons/react';
import { useAuthStore } from '../stores/authStore';
import { useLanguageStore, SUPPORTED_LANGUAGES, type LanguageCode } from '../stores/languageStore';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { isLocalDeployment } from '../config';
import { licenseApi, authApi } from '../services/api';
import { LicensePage } from './LicensePage';
import { useToastActions } from '../components/ui/Toast';
import { getAvatarUrl } from '../utils/avatar';
import { useNavigate } from 'react-router-dom';
import { getRegionProfile } from '../config/region';
import { getPurchaseUrl } from '../config/purchase';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';

// 格式化字节
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

interface SettingsItem {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string; weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone' }>;
  iconColor: string;
  iconBgColor: string;
  onClick: () => void;
  rightContent?: React.ReactNode;
}

export function SettingsPage() {
  const { t } = useTranslation();
  const getPlanName = (plan: string) => t(`nav.planNames.${plan}`) || plan;
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { subscription } = useSubscriptionStore();
  const profile = getRegionProfile();
  const isCN = profile.region === 'cn';
  const [currentView, setCurrentView] = useState<'main' | 'profile' | 'license' | 'activationCode'>('main');
  // 语言状态 - 使用全局状态
  const { language, setLanguage } = useLanguageStore();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [languageDropdownPosition, setLanguageDropdownPosition] = useState<{ top: number; right: number } | null>(null);
  const languageTriggerRef = useRef<HTMLDivElement>(null);

  const currentLanguage = SUPPORTED_LANGUAGES.find(l => l.code === language);

  const openLanguageDropdown = () => {
    if (languageTriggerRef.current) {
      const rect = languageTriggerRef.current.getBoundingClientRect();
      const menuHeight = 180;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openAbove = spaceBelow < menuHeight && spaceAbove > spaceBelow;
      setLanguageDropdownPosition({
        top: openAbove ? rect.top - menuHeight - 8 : rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
      setShowLanguageDropdown(true);
    }
  };

  const closeLanguageDropdown = () => {
    setShowLanguageDropdown(false);
    setLanguageDropdownPosition(null);
  };

  // 点击外部关闭语言下拉框
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showLanguageDropdown && !languageTriggerRef.current?.contains(target) && !target.closest('[data-language-dropdown]')) {
        closeLanguageDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLanguageDropdown]);

  // License 状态（本地部署时显示）
  const [licenseStatus, setLicenseStatus] = useState<string>('');

  // 加载 License 状态（本地部署时）
  useEffect(() => {
    if (isLocalDeployment) {
      licenseApi.getLicenseStatus().then((res) => {
        if (res.code === 0 && res.data) {
          const statusMap: Record<string, string> = {
            active: '已激活',
            grace_period: '宽限期',
            expired: '已过期',
            revoked: '已吊销',
            invalid: '无效',
          };
          setLicenseStatus(statusMap[res.data.license.status] || '未激活');
        }
      }).catch(() => {
        setLicenseStatus('未激活');
      });
    }
  }, []);

  // 设置项配置（本地化部署时隐藏订阅管理）
  const licenseItem: SettingsItem = {
    id: 'license',
    title: t('settings.license'),
    description: licenseStatus || t('settings.licenseDesc'),
    icon: Key,
    iconColor: 'text-amber-600',
    iconBgColor: 'bg-amber-100',
    onClick: () => setCurrentView('license'),
  };

  // CN Region: Special Code Activation Item
  const activationCodeItem: SettingsItem = {
    id: 'activationCode',
    title: t('settings.activationCode'),
    description: t('settings.activationCodeDesc'),
    icon: Ticket,
    iconColor: 'text-purple-600',
    iconBgColor: 'bg-purple-100',
    onClick: () => setCurrentView('activationCode'),
  };

  const allSettingsItems: SettingsItem[] = [
    ...(isLocalDeployment ? [licenseItem] : []),
    {
      id: 'profile',
      title: t('settings.userInfo'),
      description: user?.nickname || user?.username || t('settings.userInfoDesc'),
      icon: User,
      iconColor: 'text-emerald-600',
      iconBgColor: 'bg-emerald-100',
      onClick: () => setCurrentView('profile'),
    },
    {
      id: 'language',
      title: t('common.language'),
      description: currentLanguage?.name || '简体中文',
      icon: Globe,
      iconColor: 'text-blue-600',
      iconBgColor: 'bg-blue-100',
      onClick: () => openLanguageDropdown(),
      rightContent: (
        <div ref={languageTriggerRef} onClick={(e) => { e.stopPropagation(); openLanguageDropdown(); }} className="flex items-center">
          <span className="text-lg mr-2">{currentLanguage?.flag}</span>
        </div>
      ),
    },
    // CN Region: Add activation code item before subscription
    ...(isCN && !isLocalDeployment ? [activationCodeItem] : []),
    // 订阅管理 - 跳转到独立页面（CN区域隐藏）
    ...(isLocalDeployment || isCN ? [] : [{
      id: 'subscription',
      title: t('settings.subscription'),
      description: t('settings.subscriptionDesc', { plan: getPlanName(subscription?.plan || 'basic'), usage: subscription?.currentMonthExecutions || 0 }),
      icon: Crown,
      iconColor: 'text-amber-600',
      iconBgColor: 'bg-amber-100',
      onClick: () => navigate('/subscription'),
      rightContent: subscription ? (
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <span>{t('settings.storage', { usage: formatBytes(subscription.currentStorageBytes) })}</span>
        </div>
      ) : undefined,
    }]),
    // 我的团队 - 跳转到独立页面
    {
      id: 'team',
      title: t('settings.myTeam'),
      description: t('settings.myTeamDesc'),
      icon: Users,
      iconColor: 'text-indigo-600',
      iconBgColor: 'bg-indigo-100',
      onClick: () => navigate('/team'),
    },
  ];

  const settingsItems = isLocalDeployment
    ? allSettingsItems.filter((item) => item.id !== 'subscription')
    : allSettingsItems;

  // 渲染子页面
  if (currentView === 'profile') {
    return <ProfilePage onBack={() => setCurrentView('main')} />;
  }
  if (currentView === 'license') {
    return <LicensePage onBack={() => setCurrentView('main')} />;
  }
  if (currentView === 'activationCode') {
    return <ActivationCodePage onBack={() => setCurrentView('main')} />;
  }

  // 主设置页面
  return (
    <div className="h-full bg-zinc-50 overflow-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 100, damping: 20 }}
        className="max-w-2xl mx-auto p-6"
      >
        {/* 页面标题 */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-zinc-900">{t('settings.title')}</h1>
          <p className="text-sm text-zinc-500 mt-1">{t('settings.subtitle')}</p>
        </div>

        {/* 设置列表 */}
        <div className="bg-white rounded-2xl border border-zinc-200/50 overflow-hidden">
          {settingsItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={item.onClick}
                className={`w-full flex items-center gap-4 p-4 text-left transition-colors hover:bg-zinc-50 ${
                  index !== settingsItems.length - 1 ? 'border-b border-zinc-100' : ''
                }`}
              >
                <div className={`w-10 h-10 rounded-xl ${item.iconBgColor} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-5 h-5 ${item.iconColor}`} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-900">{item.title}</p>
                  <p className="text-xs text-zinc-500 truncate">{item.description}</p>
                </div>
                {item.rightContent}
                <CaretRight className="w-4 h-4 text-zinc-400 flex-shrink-0" />
              </button>
            );
          })}
        </div>

        {/* 语言下拉 - Portal 渲染，避免 overflow 裁剪 */}
        {showLanguageDropdown && languageDropdownPosition && createPortal(
          <motion.div
            data-language-dropdown
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="fixed w-48 bg-white rounded-xl border border-zinc-200 shadow-xl shadow-zinc-900/10 py-1 z-[110]"
            style={{
              top: languageDropdownPosition.top,
              right: languageDropdownPosition.right,
            }}
          >
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={(e) => {
                  e.stopPropagation();
                  setLanguage(lang.code as LanguageCode);
                  closeLanguageDropdown();
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                  language === lang.code ? 'bg-emerald-50 text-emerald-700' : 'text-zinc-700 hover:bg-zinc-50'
                }`}
              >
                <span className="text-lg">{lang.flag}</span>
                <span>{lang.name}</span>
                {language === lang.code && (
                  <Check className="w-4 h-4 ml-auto" weight="bold" />
                )}
              </button>
            ))}
          </motion.div>,
          document.body
        )}

        {/* 版本信息 */}
        <div className="mt-8 text-center">
          <p className="text-xs text-zinc-400">{t('settings.version')}</p>
          <p className="text-xs text-zinc-400 mt-1">{t('settings.copyright')}</p>
        </div>
      </motion.div>
    </div>
  );
}

// ==================== 用户信息编辑页面 ====================

function ProfilePage({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation();
  const { user, setUser } = useAuthStore();
  const { success: toastSuccess, error: toastError } = useToastActions();
  const [isEditing, setIsEditing] = useState(false);
  const [nickname, setNickname] = useState(user?.nickname || '');
  const [email, setEmail] = useState(user?.email || '');
  const [avatar, setAvatar] = useState(user?.avatar || '');
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSave = async () => {
    if (!nickname.trim()) {
      toastError(t('settings.nicknameRequired'));
      return;
    }

    setIsSaving(true);
    try {
      const response = await authApi.updateProfile({ nickname: nickname.trim() });
      if (response.code === 0) {
        // 更新本地状态
        if (user) {
          setUser({ ...user, nickname: nickname.trim() });
        }
        toastSuccess(t('settings.saveSuccess'));
        setIsEditing(false);
      } else {
        toastError(response.message || t('common.error'));
      }
    } catch (error) {
      toastError(t('common.error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      toastError(t('common.error'));
      return;
    }

    // 验证文件大小 (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toastError(t('common.error'));
      return;
    }

    setIsUploading(true);
    try {
      // 上传到服务器
      const formData = new FormData();
      formData.append('avatar', file);
      
      const token = localStorage.getItem('token') || '';
      const baseURL = (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL || '/api/v1';
      
      const response = await fetch(`${baseURL}/auth/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('上传失败');
      }

      const result = await response.json();
      if (result.code === 0) {
        // 将相对路径转换为完整 URL
        const avatarPath = result.data.avatarUrl;
        const newAvatarUrl = avatarPath.startsWith('http') 
          ? avatarPath 
          : `${baseURL.replace('/api/v1', '')}${avatarPath}`;
        setAvatar(newAvatarUrl);
        // 同步更新 authStore 中的用户信息
        if (user) {
          setUser({ ...user, avatar: newAvatarUrl });
        }
        toastSuccess(t('common.success'));
      } else {
        throw new Error(result.message || '上传失败');
      }
    } catch (error) {
      toastError(t('common.error'));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="h-full bg-zinc-50 overflow-auto">
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="max-w-2xl mx-auto p-6"
      >
        {/* 头部 */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={onBack}
            className="p-2 hover:bg-zinc-200 rounded-lg transition-colors"
          >
            <CaretRight className="w-5 h-5 text-zinc-600 rotate-180" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-zinc-900">{t('settings.profileTitle')}</h1>
            <p className="text-sm text-zinc-500">{t('settings.profileSubtitle')}</p>
          </div>
        </div>

        {/* 内容 */}
        <div className="bg-white rounded-2xl border border-zinc-200/50 p-6 space-y-6">
          {/* 头像上传 */}
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <div 
                onClick={handleAvatarClick}
                className="w-24 h-24 rounded-2xl bg-gradient-to-br from-emerald-100 to-emerald-50 flex items-center justify-center border-2 border-emerald-200 cursor-pointer overflow-hidden group"
              >
                {avatar ? (
                  <img 
                    src={getAvatarUrl(avatar)} 
                    alt="Avatar" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-12 h-12 text-emerald-600" weight="fill" />
                )}
                
                {/* 悬停遮罩 */}
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  {isUploading ? (
                    <Spinner className="w-6 h-6 text-white animate-spin" />
                  ) : (
                    <Upload className="w-6 h-6 text-white" />
                  )}
                </div>
              </div>
              
              {/* 编辑按钮 */}
              <button
                onClick={handleAvatarClick}
                disabled={isUploading}
                className="absolute -bottom-1 -right-1 w-8 h-8 bg-zinc-900 text-white rounded-lg flex items-center justify-center shadow-lg hover:bg-zinc-800 transition-colors disabled:opacity-50"
              >
                {isUploading ? (
                  <Spinner className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
              </button>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/jpg"
              onChange={handleFileChange}
              className="hidden"
            />
            
            <p className="text-xs text-zinc-400">{t('settings.avatarHint')}</p>
          </div>

          {/* 表单 */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-2 block">{t('auth.nickname')}</label>
              <input
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                disabled={!isEditing}
                placeholder={t('settings.nicknamePlaceholder')}
                className="w-full px-3 py-2.5 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:bg-zinc-50 disabled:text-zinc-500"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-2 block">{t('auth.email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2.5 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:bg-zinc-50 disabled:text-zinc-500"
              />
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="flex justify-end gap-3 pt-4 border-t border-zinc-100">
            {isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                >
                  {isSaving ? t('common.saving') : t('common.save')}
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-zinc-900 text-white text-sm font-medium rounded-lg hover:bg-zinc-800 transition-colors"
              >
                {t('settings.editProfile')}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ==================== 激活码页面（CN 区域）====================

function ActivationCodePage({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation();
  const { success: toastSuccess, error: toastError } = useToastActions();
  const { fetchSubscription } = useSubscriptionStore();
  const [code, setCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    plan?: string;
    duration?: number;
    message?: string;
  } | null>(null);

  const handleValidate = async () => {
    if (!code.trim()) {
      toastError(t('auth.codeRequired'));
      return;
    }

    setIsValidating(true);
    setValidationResult(null);

    try {
      const response = await authApi.validateSpecialCode(code.trim());
      if (response.code === 0 && response.data) {
        setValidationResult({
          valid: response.data.valid,
          plan: response.data.plan,
          duration: response.data.durationMonths,
          message: response.data.message,
        });
        if (response.data.valid) {
          toastSuccess(t('auth.codeValid', { 
            plan: response.data.plan, 
            duration: response.data.durationMonths 
          }));
        }
      } else {
        setValidationResult({
          valid: false,
          message: response.message || t('auth.invalidCode'),
        });
      }
    } catch (err: unknown) {
      setValidationResult({
        valid: false,
        message: err instanceof Error ? err.message : t('auth.codeValidationFailed'),
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleActivate = async () => {
    if (!code.trim()) {
      toastError(t('auth.codeRequired'));
      return;
    }

    setIsActivating(true);

    try {
      const response = await authApi.activateSpecialCode(code.trim());
      if (response.code === 0 && response.data) {
        toastSuccess(t('auth.codeActivated', { 
          plan: response.data.plan,
          duration: response.data.durationMonths 
        }));
        // Refresh subscription data
        await fetchSubscription();
        // Go back after successful activation
        setTimeout(() => {
          onBack();
        }, 1500);
      } else {
        toastError(response.message || t('auth.codeActivationFailed'));
      }
    } catch (err: unknown) {
      toastError(err instanceof Error ? err.message : t('auth.codeActivationFailed'));
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <div className="h-full bg-zinc-50 overflow-auto">
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="max-w-2xl mx-auto p-6"
      >
        {/* 头部 */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={onBack}
            className="p-2 hover:bg-zinc-200 rounded-lg transition-colors"
          >
            <CaretRight className="w-5 h-5 text-zinc-600 rotate-180" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-zinc-900">{t('settings.activationCodeTitle')}</h1>
            <p className="text-sm text-zinc-500">{t('settings.activationCodeSubtitle')}</p>
          </div>
        </div>

        {/* 内容 */}
        <div className="bg-white rounded-2xl border border-zinc-200/50 p-6 space-y-6">
          {/* 说明 */}
          <div className="bg-purple-50 border border-purple-100 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Ticket className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-purple-800">
                <p className="font-medium mb-1">{t('settings.activationCodeInfo')}</p>
                <p>{t('settings.activationCodeInfoDesc')}</p>
              </div>
            </div>
          </div>

          {/* 购买链接 */}
          <div className="flex items-center justify-between py-3 border-b border-zinc-100">
            <span className="text-sm text-zinc-600">{t('settings.buyActivationCode')}</span>
            <a 
              href={getPurchaseUrl()}
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
            >
              {t('settings.goToXiaohongshu')} →
            </a>
          </div>

          {/* 输入框 */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-zinc-700 mb-2 block">
                {t('settings.enterActivationCode')}
              </label>
              <div className="flex items-stretch gap-2">
                <Input
                  type="text"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    setValidationResult(null);
                  }}
                  leftIcon={<Ticket className="w-5 h-5" />}
                  placeholder={t('settings.activationCodePlaceholder')}
                  className="flex-1"
                />
                <Button
                  variant="secondary"
                  onClick={handleValidate}
                  isLoading={isValidating}
                  disabled={!code.trim()}
                  className="whitespace-nowrap px-4 h-auto"
                >
                  {t('auth.validate')}
                </Button>
              </div>
            </div>

            {/* 验证结果 */}
            {validationResult && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-xl ${
                  validationResult.valid 
                    ? 'bg-emerald-50 border border-emerald-100' 
                    : 'bg-red-50 border border-red-100'
                }`}
              >
                {validationResult.valid ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <Check className="w-5 h-5" weight="bold" />
                      <span className="font-medium">{t('auth.codeValid')}</span>
                    </div>
                    <div className="text-sm text-emerald-800 space-y-1">
                      <p>{t('auth.codeValidDescription', { 
                        plan: validationResult.plan,
                        duration: validationResult.duration 
                      })}</p>
                    </div>
                    <Button
                      variant="primary"
                      onClick={handleActivate}
                      isLoading={isActivating}
                      className="w-full"
                    >
                      {t('auth.activateNow')}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-600">
                    <WarningCircle className="w-5 h-5" />
                    <span>{validationResult.message}</span>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
